import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Button, Empty, Input, Select, Table, Tag, Tooltip, message } from "antd";
import { LinkOutlined, RedoOutlined, StopOutlined } from "@ant-design/icons";

import { getInvites, resendInvite, revokeInvite } from "./controller";

const STATE_OPTIONS = [
  { label: "All statuses", value: "" },
  { label: "Pending", value: "pending" },
  { label: "Accepted", value: "accepted" },
  { label: "Cancelled", value: "cancelled" },
];

function emailInitials(email) {
  if (!email) return "—";
  const local = String(email).split("@")[0].replace(/[^a-zA-Z0-9]+/g, " ").trim();
  const parts = local.split(/\s+/).filter(Boolean);
  const i = ((parts[0] || "")[0] || "") + (parts[1] ? parts[1][0] : "");
  return (i || email[0] || "?").toUpperCase();
}

function fmtDate(s) {
  if (!s) return "—";
  const d = new Date(s);
  if (isNaN(d.getTime())) return "—";
  return d.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
}

// The accounts Invite has no created timestamp — status is derived from
// state/is_used plus expiry. Accepted (registered) > cancelled > expired > pending.
function inviteStatus(r) {
  if (r.state === "accepted" || r.is_used) return { key: "accepted", label: "Accepted" };
  if (r.state === "cancelled") return { key: "cancelled", label: "Cancelled" };
  const exp = r.expiration_date ? new Date(r.expiration_date) : null;
  if (exp && !isNaN(exp.getTime()) && exp.getTime() < Date.now()) {
    return { key: "expired", label: "Expired" };
  }
  return { key: "pending", label: "Pending" };
}

/**
 * "Invites" tab of the Platform CRM — every invite sent (pending, accepted,
 * cancelled), which the practices list can't show until an invite is accepted.
 * Consumes the existing /crm/invites/ endpoints (list + resend + revoke).
 *
 * `type` scopes the list to one invite type ("customer" by default on the server,
 * or "ftc" for the FTC Users page); it also drives the per-row sub-label (customer
 * invites show a free-trial tag, FTC invites show the offices they grant).
 */
export default function CrmInvites({ reloadTick, onCount, type, initialSearch }) {
  const [rows, setRows] = useState([]);
  const [count, setCount] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [search, setSearch] = useState("");
  const [state, setState] = useState("");
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(""); // "<id>:<action>" while a row action is in flight

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getInvites({
        page,
        pageSize,
        search: search || undefined,
        state: state || undefined,
        type: type || undefined,
      });
      if (res.status >= 200 && res.status < 300 && res.json) {
        const list = Array.isArray(res.json.results) ? res.json.results : [];
        const c = typeof res.json.count === "number" ? res.json.count : list.length;
        setRows(list);
        setCount(c);
        // The badge is what still needs anything — the server's "redeemable"
        // count for this type — not the size of whatever filter is showing.
        // `count` stays the filtered total because the pager needs it.
        if (onCount) onCount(typeof res.json.pending_count === "number" ? res.json.pending_count : c);
      } else {
        setRows([]);
        setCount(0);
        if (onCount) onCount(0);
      }
    } catch (e) {
      setRows([]);
      setCount(0);
    } finally {
      setLoading(false);
    }
  }, [page, pageSize, search, state, type, onCount]);

  useEffect(() => {
    load();
  }, [load, reloadTick]);

  // The Today tab's "Open" on an invite lands here narrowed to that address.
  useEffect(() => {
    if (initialSearch !== undefined && initialSearch !== null) {
      setPage(1);
      setSearch(initialSearch);
    }
  }, [initialSearch]);

  const rowBusy = (id) => busy.startsWith(id + ":");

  const copyLink = async (r) => {
    if (!r.invite_link) {
      message.error("No invite link available for this invite.");
      return;
    }
    try {
      await navigator.clipboard.writeText(r.invite_link);
      message.success("Invite link copied to clipboard.");
    } catch (e) {
      message.warning(r.invite_link, 6); // clipboard blocked — surface it to copy manually
    }
  };

  const doResend = async (r) => {
    setBusy(r.id + ":resend");
    const res = await resendInvite(r.id);
    setBusy("");
    if (res.status >= 200 && res.status < 300) {
      message.success("Invite re-sent to " + r.receiver_email + ".");
      load();
    } else {
      message.error((res.json && res.json.detail) || "Couldn't resend the invite.");
    }
  };

  const doRevoke = async (r) => {
    setBusy(r.id + ":revoke");
    const res = await revokeInvite(r.id);
    setBusy("");
    if (res.status >= 200 && res.status < 300) {
      message.success("Invite revoked.");
      load();
    } else {
      message.error((res.json && res.json.detail) || "Couldn't revoke the invite.");
    }
  };

  const columns = useMemo(
    () => [
      {
        title: "Recipient",
        render: (_, r) => (
          <div className="crm-cell-practice">
            <span className="crm-avatar-sm">{emailInitials(r.receiver_email)}</span>
            <div className="crm-cell-stack">
              <div className="crm-name">{r.receiver_email || "—"}</div>
              <div className="crm-sub">
                {r.invite_type || "invite"}
                {type === "ftc" ? (
                  (r.offices && r.offices.length) ? (
                    r.offices.map((o) => (
                      <Tag key={o.id} style={{ marginLeft: 6 }}>{o.name}</Tag>
                    ))
                  ) : (
                    <Tag style={{ marginLeft: 6 }}>No offices</Tag>
                  )
                ) : (
                  r.trial_enabled ? <Tag color="gold" style={{ marginLeft: 6 }}>Free trial</Tag> : null
                )}
              </div>
            </div>
          </div>
        ),
      },
      {
        title: "Status",
        width: 132,
        render: (_, r) => {
          const s = inviteStatus(r);
          return <span className={`crm-ipill ${s.key}`}>{s.label}</span>;
        },
      },
      {
        title: "Expires",
        width: 140,
        render: (_, r) => {
          const s = inviteStatus(r);
          return (
            <span className={`crm-exp${s.key === "expired" ? " past" : ""}`}>
              {fmtDate(r.expiration_date)}
            </span>
          );
        },
      },
      {
        title: "Invited by",
        render: (_, r) => <span className="crm-sub">{r.inviter || "—"}</span>,
      },
      {
        title: "",
        key: "actions",
        width: 236,
        align: "right",
        render: (_, r) => {
          const s = inviteStatus(r);
          const accepted = s.key === "accepted";
          return (
            <div className="crm-row-actions">
              <Tooltip title={r.invite_link ? "Copy invite link" : "No link"}>
                <Button
                  size="small"
                  icon={<LinkOutlined />}
                  disabled={!r.invite_link}
                  onClick={() => copyLink(r)}
                />
              </Tooltip>
              <Button
                size="small"
                icon={<RedoOutlined />}
                loading={busy === r.id + ":resend"}
                disabled={accepted || rowBusy(r.id)}
                onClick={() => doResend(r)}
              >
                Resend
              </Button>
              <Button
                size="small"
                danger
                icon={<StopOutlined />}
                loading={busy === r.id + ":revoke"}
                disabled={accepted || s.key === "cancelled" || rowBusy(r.id)}
                onClick={() => doRevoke(r)}
              >
                Revoke
              </Button>
            </div>
          );
        },
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [busy]
  );

  return (
    <>
      <div className="crm-toolbar">
        <Input.Search
          // Uncontrolled on purpose (typing must not refetch); re-keyed when the
          // Today tab hands us an address so the box shows what the list filters by.
          key={initialSearch || "search"}
          defaultValue={initialSearch || ""}
          allowClear
          placeholder="Search recipient email"
          style={{ maxWidth: 340 }}
          onSearch={(v) => {
            setPage(1);
            setSearch(v);
          }}
        />
        <Select
          style={{ minWidth: 190 }}
          value={state}
          options={STATE_OPTIONS}
          onChange={(v) => {
            setPage(1);
            setState(v);
          }}
        />
      </div>

      <div className="crm-table-card">
        <Table
          rowKey="id"
          columns={columns}
          dataSource={rows}
          loading={loading}
          locale={{ emptyText: <Empty description="No invites sent yet." /> }}
          pagination={{
            current: page,
            pageSize: pageSize,
            total: count,
            showSizeChanger: true,
            pageSizeOptions: ["10", "25", "50", "100"],
            onChange: (nextPage, nextSize) => {
              setPage(nextPage);
              setPageSize(nextSize);
            },
          }}
        />
      </div>
    </>
  );
}
